import { Alert, Grid, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";

import {
  AutoComplete,
  DateInput,
  FormContainer,
  FormInput,
  SwitchInput,
  Treturn,
  useFetch,
} from "@4uhub/lib4uhub";
import { useCallback, useState } from "react";
import FormTextEditor from "../../../../components/UI/TextEditor/FormTextEditor";
import { TFormsForm, formSchema } from "./formSchema";
import {
  createEvaluationForm,
  getEvaluationFormById,
  updateEvaluationFormById,
} from "../../../../services/evaluationForm.service";
import { SiteAppExtension } from "./SiteAppExtension/SiteAppExtension";
import { IQuestionList } from "../../../../models/questions";
import { SelectQuestions } from "./SelectQuestions/SelectQuestions";
import { listMainGenerics } from "../../../../services/generic.service";
import { ShowLinkFields } from "./ShowLinkFields";

const translationPath = "page.evaluation.forms.";

const Form = () => {
  const { t } = useTranslation();

  const [defaultQuestions, setDefaultQuestions] = useState<IQuestionList[]>([]);
  const [released, setReleased] = useState(false);

  const { sendRequest: save, loading: sLoading } =
    useFetch(createEvaluationForm);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateEvaluationFormById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getEvaluationFormById,
  );

  const saveHandler = (v: TFormsForm) =>
    save({
      ...v,
      questions: v.evaluationFormQuestions.map((q) => ({
        questionId: q.id,
        displayOrder: Number(q.displayOrder),
      })),
      siteAppExtensionId: v.siteAppExtension.id,
      initialDate: v.initialDate.toISOString().substring(0, 10),
      endDate: v.endDate.toISOString().substring(0, 10),
      typeFormId: v.typeForm.id,
      layoutFormId: v.layoutForm.id,
      answeredFormId: v.answeredForm.id,
      showLinkOnEnd: v.showLinkOnEnd,
      showLinkButtonLabel: v.showLinkButtonLabel,
      showLinkDescription: v.showLinkDescription,
      showLinkLink: v.showLinkLink,
      showLinkGreaterThen: Number(v.showLinkGreaterThen),
      responseTimeLimitHours: v.responseTimeLimitHours
        ? Number(v.responseTimeLimitHours)
        : null,
    });

  const updateHandler = (v: TFormsForm, id: string) =>
    update({
      item: {
        ...v,
        questions: v.evaluationFormQuestions.map((q) => ({
          questionId: q.id,
          displayOrder: Number(q.displayOrder),
        })),
        siteAppExtensionId: v.siteAppExtension.id,
        initialDate: v.initialDate.toISOString().substring(0, 10),
        endDate: v.endDate.toISOString().substring(0, 10),
        typeFormId: v.typeForm.id,
        layoutFormId: v.layoutForm.id,
        answeredFormId: v.answeredForm.id,
        showLinkOnEnd: v.showLinkOnEnd,
        showLinkButtonLabel: v.showLinkButtonLabel,
        showLinkDescription: v.showLinkDescription,
        showLinkLink: v.showLinkLink,
        showLinkGreaterThen: Number(v.showLinkGreaterThen),
        responseTimeLimitHours: v.responseTimeLimitHours
          ? Number(v.responseTimeLimitHours)
          : null,
      },
      id: id,
    });

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TFormsForm>> => {
      const { data } = await get(id);
      if (data) {
        setReleased(data.released);
        setDefaultQuestions(
          data.evaluationFormQuestions.map((q) => ({
            ...q,
            id: q.question.id,
            displayOrder: q.displayOrder.toString(),
            questionEvaluationType: q.question.questionEvaluationType,
            title: q.question.title,
            description: "",
            integrationId: "",
          })),
        );

        return {
          data: {
            ...data,
            integrationId: data.integrationId,
            showLinkOnEnd: data.showLinkOnEnd ?? false,
            showLinkGreaterThen: data.showLinkGreaterThen?.toString(),
            responseTimeLimitHours: data.responseTimeLimitHours?.toString(),
            showLinkLink: data.showLinkLink,
            showLinkDescription: data.showLinkDescription,
            showLinkButtonLabel: data.showLinkButtonLabel,
            evaluationFormQuestions: data.evaluationFormQuestions.map((q) => ({
              id: q.question.id,
              title: q.question.title,
              type: q.question.questionEvaluationType,
              displayOrder: q.displayOrder.toString(),
            })),
            siteAppExtension: {
              id: data.siteAppExtension.id,
              name: data.siteAppExtension.siteApp.name,
            },
            initialDate: new Date(data.initialDate),
            endDate: new Date(data.endDate),
            typeForm: data.typeForm,
            layoutForm: data.layoutForm,
            answeredForm: data.answeredForm,
          },
          success: true,
        };
      }
      return {
        data: null,
        success: false,
      };
    },
    [get],
  );

  const loading = sLoading || uLoading || gLoading;

  return (
    <FormContainer<TFormsForm>
      saveFunction={saveHandler}
      updateFunction={updateHandler}
      getFunction={getHandler}
      loading={loading}
      title={t(translationPath + "form")}
      schema={formSchema}
      defaultValues={{ showLinkOnEnd: false }}
      subtitle={t(translationPath + "new_form")}
      subtitleWatchField={"title"}
      fields={() => (
        <Grid container spacing={2} mb={10}>
          {released && (
            <Grid item xs={12}>
              <Alert severity="warning">
                {t(translationPath + "released_form_warning")}
              </Alert>
            </Grid>
          )}
          <Grid item xs={12} sm={12} textAlign="end">
            <SwitchInput
              name="enable"
              label={t(translationPath + "enable") || "Enable"}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <AutoComplete
              getOptionLabel={({ name }) => name}
              size="small"
              label={t(translationPath + "type_form")}
              name="typeForm"
              request={listMainGenerics}
              params={{ Identifier: "EvaluationTypeForm" }}
              disable={released}
            />
          </Grid>
          <Grid item xs={12} sm={5}>
            <FormInput
              size="small"
              name="title"
              fullWidth
              label={t(translationPath + "title") || "Title"}
            />
          </Grid>
          <Grid item xs={12} sm={2}>
            <FormInput
              size="small"
              name="integrationId"
              fullWidth
              disabled={released}
              label={
                t(translationPath + "integration_id") || "Integration Code"
              }
            />
          </Grid>
          <Grid
            item
            xs={12}
            component="fieldset"
            disabled={released}
            sx={{ border: 0, p: 0, m: 0, minWidth: 0 }}
          >
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <DateInput
                  name="initialDate"
                  label={t(translationPath + "initial_date")}
                  disabled={released}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <DateInput
                  name="endDate"
                  label={t(translationPath + "end_date")}
                  disabled={released}
                />
              </Grid>

              <SiteAppExtension disabled={released} />

              <Grid item xs={12} sm={12}>
                <FormInput
                  size="small"
                  name="description"
                  fullWidth
                  disabled={released}
                  label={t(translationPath + "description") || "Description"}
                  multiline
                  minRows={4}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AutoComplete
                  getOptionLabel={({ name }) => name}
                  size="small"
                  label={t(translationPath + "layout_form")}
                  name="layoutForm"
                  request={listMainGenerics}
                  params={{ Identifier: "LayoutForm" }}
                  disable={released}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <AutoComplete
                  getOptionLabel={({ name }) => name}
                  size="small"
                  label={t(translationPath + "answered_form")}
                  name="answeredForm"
                  request={listMainGenerics}
                  params={{ Identifier: "AnsweredForm" }}
                  disable={released}
                />
              </Grid>

              <Grid
                item
                xs={12}
                sm={12}
                sx={{ display: "flex", flexDirection: "column", gap: 1 }}
              >
                <Typography>
                  {t(translationPath + "visual_description")}
                </Typography>
                <FormTextEditor
                  name="visualDescription"
                  readOnly={released}
                  menuBarProps={{
                    fontSize: true,
                    selectColor: false,
                    textFormatting: true,
                    markFormatting: false,
                    linkFormatting: false,
                  }}
                />
              </Grid>

              <ShowLinkFields disabled={released} />

              <Grid item xs={12} sm={6}>
                <FormInput
                  size="small"
                  name="responseTimeLimitHours"
                  fullWidth
                  type="number"
                  disabled={released}
                  label={t(translationPath + "response_time_limit_hours")}
                />
              </Grid>

              <SelectQuestions
                defaultQuestions={defaultQuestions}
                disabled={released}
              />
            </Grid>
          </Grid>
        </Grid>
      )}
    />
  );
};

export default Form;
